import RenderManager from "./render-manager";

import FRAGMENT_SHADER from "./shaders/mesh-nested-material.fs";
import VERTEX_SHADER from "./shaders/mesh-nested-material.vs";
import AssetLibrary from "../assets/asset-library";

class MeshNestedMaterial extends THREE.ShaderMaterial {
    constructor(parameters = {}) {
        const base = THREE.ShaderLib.standard;

        const uniforms = THREE.UniformsUtils.merge([base.uniforms, {
            smoothClipEnd: { value: -10 },
            smoothClipStart: { value: -11 },
            boundingScale: { value: new THREE.Vector3() }
        }]);

        uniforms.layerDistances = { value: [] };
        uniforms.layerMaps = { value: [] };
        uniforms.layerNormalMaps = { value: [] };
        uniforms.layerAvailable = { value: [] };
        uniforms.layerRepeat = { value: new THREE.Vector2(0, 10) };

        // if (parameters.map) uniforms.map = { value: parameters.map };
        // if (parameters.normalMap) uniforms.normalMap = { value: parameters.normalMap };
        if (parameters.envMap) uniforms.envMap = { value: parameters.envMap };

        let layers = [];

        if (parameters.layers) {
            layers = parameters.layers.sort((a, b) => b.distance.x - a.distance.x);

            layers.forEach(layer => {
                uniforms.layerDistances.value.push(layer.distance);
                uniforms.layerMaps.value.push(layer.map || null);
                uniforms.layerNormalMaps.value.push(layer.normalMap || null);
                uniforms.layerAvailable.value.push(layer.map ? 1 : 0, layer.normalMap ? 1 : 0);
            });
        }

        const layerCount = Math.min(layers.length, Math.floor(RenderManager.maxTextureUnits / 2 - 1));

        const defines = {
            STANDARD: "",
            LAYER_COUNT: layerCount
        };

        if (layers.length > 0) {
            defines.USE_MAP = "";
            defines.USE_NORMALMAP = "";
        }

        super({
            uniforms: uniforms,
            defines: defines,
            fragmentShader: FRAGMENT_SHADER,
            vertexShader: VERTEX_SHADER,
            wireframe: true,
            extensions: {
                derivatives: true
            }
        });

        this.materialType = "nested";

        this.color = new THREE.Color(0xffffff); // diffuse
        this.roughness = 1;
        this.metalness = 0;

        this.map = null;

        this.lightMap = null;
        this.lightMapIntensity = 1.0;

        this.aoMap = null;
        this.aoMapIntensity = 1.0;

        this.emissive = new THREE.Color(0x000000);
        this.emissiveIntensity = 1.0;
        this.emissiveMap = null;

        this.bumpMap = null;
        this.bumpScale = 1;

        this.normalMap = null;
        this.normalScale = new THREE.Vector2(1, 1);

        this.displacementMap = null;
        this.displacementScale = 1;
        this.displacementBias = 0;

        this.roughnessMap = null;

        this.metalnessMap = null;

        this.alphaMap = null;

        this.envMap = null;
        this.envMapIntensity = 1.0;

        this.refractionRatio = 0.98;

        this.wireframe = false;
        this.wireframeLinewidth = 1;
        this.wireframeLinecap = "round";
        this.wireframeLinejoin = "round";

        this.skinning = false;
        this.morphTargets = false;
        this.morphNormals = false;

        this.smoothClipEnd = uniforms.smoothClipEnd;
        this.smoothClipStart = uniforms.smoothClipStart;
        this._smoothClipEnabled = false;


        delete parameters.layers;
        this.setValues(parameters);

        this.isMeshStandardMaterial = true;
        this.lights = true;
        this.fog = true;

        this.isMeshNestedMaterial = true;

        /**
         * @type {any[]}
         */
        this._layers = layers;
        this._layerCount = layerCount;

        if (layers.length === 0) this.addEmptyLayer();

        this._lastLayerCount = layers.length;
    }

    get layers() { return this._layers; }

    copy(source) {
        THREE.ShaderMaterial.prototype.copy.call(this, source);
        this.color.copy(source.color)
        this.roughness = source.roughness;
        this.metalness = source.metalness;

        this.map = source.map;

        this.lightMap = source.lightMap;
        this.lightMapIntensity = source.lightMapIntensity;

        this.aoMap = source.aoMap;
        this.aoMapIntensity = source.aoMapIntensity;

        this.emissive.copy(source.emissive);
        this.emissiveIntensity = source.emissiveIntensity;
        this.emissiveMap = source.emissiveMap;

        this.bumpMap = source.bumpMap;
        this.bumpScale = source.bumpScale;

        this.normalMap = source.normalMap;
        this.normalScale.copy(source.normalScale);

        this.displacementMap = source.displacementMap;
        this.displacementScale = source.displacementScale;
        this.displacementBias = source.displacementBias;

        this.roughnessMap = source.roughnessMap;

        this.metalnessMap = source.metalnessMap;

        this.alphaMap = source.alphaMap;

        this.envMap = source.envMap;
        this.envMapIntensity = source.envMapIntensity;

        this.refractionRatio = source.refractionRatio;

        this.wireframe = source.wireframe;
        this.wireframeLinewidth = source.wireframeLinewidth;
        this.wireframeLinecap = source.wireframeLinecap;
        this.wireframeLinejoin = source.wireframeLinejoin;

        this.skinning = source.skinning;
        this.morphTargets = source.morphTargets;
        this.morphNormals = source.morphNormals;

        this.smoothClipEnd = source.smoothClipEnd;
        this.smoothClipStart = source.smoothClipStart;
        this._smoothClipEnabled = source._smoothClipEnabled;


        this.isMeshStandardMaterial = source.isMeshStandardMaterial;
        this.lights = source.lights
        this.fog = source.fog;

        return this;
    }

    pickBestLayers(best, min, max, force = false) {
        const validLayers = this._layers.filter((x, i) => (x.distance || i === 0) /*&& (x.map || x.normalMap)*/);
        const lenValidLayers = validLayers.length;

        const layerCount = Number.isFinite(min) ? Math.min(lenValidLayers, Math.floor(RenderManager.maxTextureUnits / 2 - 1)) : 1;

        if (this._layerCount !== layerCount) {
            this._layerCount = layerCount;

            this.defines.LAYER_COUNT = layerCount

            if (layerCount > 0) {
                this.defines.USE_MAP = "";
                this.defines.USE_NORMALMAP = "";
            }

            this.needsUpdate = true;
            force = true;
        }


        const uniforms = this.uniforms;
        if (this._layerCount <= lenValidLayers && !force) {

            [uniforms.layerMaps.value, uniforms.layerNormalMaps.value].forEach(layers => {
                layers.forEach(map => {
                    if (map && (map.wrapT !== THREE.RepeatWrapping || map.wrapS !== THREE.RepeatWrapping)) {
                        map.wrapT = map.wrapS = THREE.RepeatWrapping;
                        map.needsUpdate = true;
                    }
                });
            });
            return;
        }

        let index = 0;
        for (let i = 0, len = lenValidLayers; i < len; i++) {
            const layer = validLayers[i];
            layer.distance = layer.distance || new THREE.Vector2();

            const distance = layer.distance;


            index = i;

            if (distance.x < (max + min) * 0.5) { break; }

        }

        if (index > 0) index = index - 1;
        if (index + this._layerCount >= lenValidLayers) index = this._layers.length - this._layerCount;
        if (index < 0) index = 0;

        const layers = this._layers.slice(index, index + this._layerCount);


        uniforms.layerDistances.value.length = 0;
        uniforms.layerMaps.value.length = 0;
        uniforms.layerNormalMaps.value.length = 0;
        uniforms.layerAvailable.value.length = 0;
        uniforms.layerRepeat.value.x = index + this._layerCount;

        layers.forEach(layer => {
            if (layer.map && (layer.map.wrapT !== THREE.RepeatWrapping || layer.map.wrapS !== THREE.RepeatWrapping)) {
                layer.map.wrapT = layer.map.wrapS = THREE.RepeatWrapping;
                layer.map.needsUpdate = true;
            }

            if (layer.normalMap && (layer.normalMap.wrapT !== THREE.RepeatWrapping || layer.normalMap.wrapS !== THREE.RepeatWrapping)) {
                layer.normalMap.wrapT = layer.normalMap.wrapS = THREE.RepeatWrapping;
                layer.normalMap.needsUpdate = true;
            }

            uniforms.layerDistances.value.push(layer.distance);
            uniforms.layerMaps.value.push(layer.map || null);
            uniforms.layerNormalMaps.value.push(layer.normalMap || null);
            uniforms.layerAvailable.value.push(layer.map ? 1 : 0, layer.normalMap ? 1 : 0);
        });
    }

    resendTexturesToGPU() {
        [this.uniforms.layerMaps, this.uniforms.layerNormalMaps]
            .forEach(uniform => uniform.value.forEach((layer, i, arr) => {
                if (!layer) return;

                arr[i] = AssetLibrary.textures[layer.uuid];

                if (arr[i] && (arr[i].wrapT !== THREE.RepeatWrapping || arr[i].wrapS !== THREE.RepeatWrapping)) {
                    arr[i].wrapT = arr[i].wrapS = THREE.RepeatWrapping;
                    arr[i].needsUpdate = true;
                }
            }));
    }

    setLayerValue(index, value) {
        const len = this._layers.length;
        const collectedXValues = this._layers.map(({ distance }) => distance.x);
        const prevValue = collectedXValues[index];

        collectedXValues[index] = value;

        if (value > prevValue) {
            for (let i = index - 1; i >= 0; i--)
                collectedXValues[i] = Math.max(value, collectedXValues[i]);
        } else {
            for (let i = index + 1; i < len; i++)
                collectedXValues[i] = Math.min(value, collectedXValues[i]);
        }

        if (len > 1)
            collectedXValues[0] = collectedXValues[1] * 2;

        // console.log(collectedXValues.join(", "));

        this.recalcLayerValues(collectedXValues);
    }

    recalcLayerValues(collectedXValues = null) {
        const len = this._layers.length;
        collectedXValues = collectedXValues || this._layers.map(({ distance }) => distance.x);

        for (let i = 0; i < len - 1; i++) {
            const xi = collectedXValues[i];
            const xj = collectedXValues[i + 1];

            const yi = Math.max(xj - (xi - xj) * 0.25, collectedXValues[i + 2] || 0);

            this._layers[i].distance.set(xi, yi);
        }

        if (len > 1) {
            this._layers[len - 1].distance.set(collectedXValues[len - 1], 0);
        } else if (len === 0) {
            this._layers[len - 1].distance.set(1, 0);
        }

    }

    addEmptyLayer() {
        const layer = { distance: new THREE.Vector2() };
        const len = this._layers.length;

        const prevLayer = this._layers[len - 1] || { distance: new THREE.Vector2(1, 0) };

        this._layers.push(layer);

        layer.distance.x = prevLayer.distance.x * 0.5;

        this.recalcLayerValues();

        // for (let i = len; i >= 1; i--) {
        //     this._layers[i].distance.copy(this._layers[i - 1].distance);
        // }

        // const clipEquation = x => Math.pow(Math.max(x, 0), 1.8) / 2;
        // const calcDistance = (x, o = new THREE.Vector2) => o.set(clipEquation(x + 0.7), clipEquation(x - 0.7));

        // calcDistance(len - 0.2, this._layers[0].distance);

        return layer;
    }

    updateLayers(object, raycaster, force = false) {
        if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();

        object.geometry.boundingBox.getSize(this.uniforms.boundingScale.value);

        this.uniforms.boundingScale.value.applyMatrix4(object.matrixWorld);

        if (!raycaster) {
            this.pickBestLayers(Infinity, Infinity, Infinity, force);
            return;
        }

        const id = raycaster.getMainRegionId(object);
        const rows = raycaster.longestRows;

        if (rows.len[id] === undefined) return;

        const x = rows.x[id], y = rows.y[id];

        const ray = raycaster.pick(new THREE.Vector2(x, y));

        if (!ray.objectId) return;

        this.pickBestLayers(ray.depth, rows.min[id], rows.max[id], force);
    }

    setSmoothClipEnabled(enabled) {
        this._smoothClipEnabled = enabled;

        if (enabled) this.defines["SMOOTH_CLIP"] = "";
        else delete this.defines["SMOOTH_CLIP"];

        this.needsUpdate = true;
    }

    setLayers(layers) { this._layers = layers; }

    get isSmoothClipEnabled() { return this._smoothClipEnabled; }
}

export default MeshNestedMaterial;
export { MeshNestedMaterial };